import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Alert,
  AlertDescription,
  AlertTitle,
} from "@/components/ui/alert";
import {
  AlertTriangle,
  CheckCircle2,
  Link2,
  Link2Off,
  ShieldCheck,
} from "lucide-react";
import {
  alpacaAccount,
  alpacaConnect,
  alpacaDisconnect,
  alpacaStatus,
  formatApiError,
  toastApiError,
} from "@/lib/api";

const fmtUsd = (v) => {
  if (v == null) return "—";
  const n = typeof v === "string" ? parseFloat(v) : v;
  if (!Number.isFinite(n)) return "—";
  return n.toLocaleString("en-US", {
    style: "currency",
    currency: "USD",
    maximumFractionDigits: 2,
  });
};

export default function AlpacaSettingsPage() {
  const [status, setStatus] = useState(null);
  const [account, setAccount] = useState(null);
  const [loading, setLoading] = useState(true);
  const [keyId, setKeyId] = useState("");
  const [secret, setSecret] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const refresh = async () => {
    try {
      const s = await alpacaStatus();
      setStatus(s);
      if (s?.connected) {
        try {
          setAccount(await alpacaAccount());
        } catch (e) {
          setAccount({ _error: formatApiError(e) });
        }
      } else {
        setAccount(null);
      }
    } catch (e) {
      toastApiError(e, `Alpaca status failed`);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    refresh();
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const trimmedKey = keyId.trim();
      const trimmedSecret = secret.trim();
      if (!trimmedKey || !trimmedSecret) {
        toast.error("Both key ID and secret are required.");
        return;
      }
      const res = await alpacaConnect({
        key_id: trimmedKey,
        secret: trimmedSecret,
      });
      toast.success(
        `Alpaca connected — account ${res.account_number || res.account_id}`,
      );
      setKeyId("");
      setSecret("");
      await refresh();
    } catch (err) {
      toastApiError(err);
    } finally {
      setSubmitting(false);
    }
  };

  const disconnect = async () => {
    if (
      !window.confirm(
        "Disconnect Alpaca? Your Alpaca Live-Sim portfolio and its trade "
        + "history in NUMIROCK will be removed. Your actual Alpaca paper "
        + "account is untouched.",
      )
    )
      return;
    setSubmitting(true);
    try {
      await alpacaDisconnect();
      toast.success("Alpaca disconnected.");
      await refresh();
    } catch (err) {
      toastApiError(err);
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div
        data-testid="alpaca-settings-loading"
        className="py-16 text-center text-sm text-[var(--aurion-muted)]"
      >
        Loading Alpaca status…
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-3xl space-y-6" data-testid="alpaca-settings-page">
      <header>
        <h1 className="font-heading text-3xl text-[var(--aurion-gold)]">
          Alpaca Paper Trading
        </h1>
        <p className="mt-1 text-sm text-[var(--aurion-muted)]">
          Connect your Alpaca <strong>paper</strong> account to add a
          live-sim portfolio to NUMIROCK. All trades — manual, verdict, and
          auto-mode — will route to Alpaca's paper API. Live-money keys are
          rejected on connect.
        </p>
      </header>

      <Alert
        className="border-[var(--aurion-border)] bg-[var(--aurion-panel)]/40"
        data-testid="alpaca-security-note"
      >
        <ShieldCheck className="h-4 w-4" />
        <AlertTitle>Your secret never leaves your computer once</AlertTitle>
        <AlertDescription className="text-xs text-[var(--aurion-muted)]">
          Keys are encrypted at rest with Fernet. NUMIROCK never returns your
          secret in any API response — even to master accounts in cloak mode.
        </AlertDescription>
      </Alert>

      {status?.connected ? (
        <Card
          className="border-emerald-500/30 bg-[var(--aurion-panel)]/60"
          data-testid="alpaca-connected-card"
        >
          <CardHeader className="flex flex-row items-center gap-3">
            <CheckCircle2 className="h-5 w-5 text-emerald-400" />
            <CardTitle className="text-base uppercase tracking-widest text-emerald-300">
              Connected
            </CardTitle>
          </CardHeader>
          <CardContent className="grid gap-3 sm:grid-cols-2">
            <Field
              label="Key ID"
              value={`•••• •••• •••• ${status.key_id_last4 || "????"}`}
              testid="alpaca-key-last4"
            />
            <Field
              label="Account number"
              value={status.account_number}
              testid="alpaca-account-number"
            />
            <Field
              label="Status"
              value={status.status || "—"}
              testid="alpaca-account-status"
            />
            <Field
              label="Connected at"
              value={
                status.connected_at
                  ? new Date(status.connected_at).toLocaleString()
                  : "—"
              }
              testid="alpaca-connected-at"
            />
            {account && !account._error ? (
              <>
                <Field
                  label="Cash"
                  value={fmtUsd(account.cash)}
                  testid="alpaca-cash"
                />
                <Field
                  label="Equity"
                  value={fmtUsd(account.equity)}
                  testid="alpaca-equity"
                />
                <Field
                  label="Buying power"
                  value={fmtUsd(account.buying_power)}
                  testid="alpaca-buying-power"
                />
                <Field
                  label="Open positions"
                  value={account.positions_count}
                  testid="alpaca-positions-count"
                />
              </>
            ) : account?._error ? (
              <div className="col-span-full">
                <Alert
                  variant="destructive"
                  data-testid="alpaca-account-error"
                >
                  <AlertTriangle className="h-4 w-4" />
                  <AlertTitle>Alpaca account read failed</AlertTitle>
                  <AlertDescription>{account._error}</AlertDescription>
                </Alert>
              </div>
            ) : null}
            <div className="col-span-full flex justify-end pt-2">
              <Button
                type="button"
                variant="outline"
                data-testid="alpaca-disconnect-btn"
                onClick={disconnect}
                disabled={submitting}
                className="border-[var(--aurion-red)]/40 text-[var(--aurion-red)] hover:bg-[var(--aurion-red)]/10"
              >
                <Link2Off className="mr-2 h-4 w-4" />
                Disconnect Alpaca
              </Button>
            </div>
          </CardContent>
        </Card>
      ) : (
        <Card
          className="border-[var(--aurion-border)] bg-[var(--aurion-panel)]/40"
          data-testid="alpaca-connect-card"
        >
          <CardHeader className="flex flex-row items-center gap-3">
            <Link2 className="h-5 w-5 text-[var(--aurion-gold)]" />
            <CardTitle className="text-base uppercase tracking-widest text-[var(--aurion-gold)]">
              Connect a paper account
            </CardTitle>
          </CardHeader>
          <CardContent>
            <form onSubmit={submit} className="space-y-4">
              <p className="text-xs text-[var(--aurion-muted)]">
                Generate a key pair at{" "}
                <a
                  href="https://app.alpaca.markets/paper/dashboard/overview"
                  target="_blank"
                  rel="noreferrer"
                  className="underline underline-offset-2"
                >
                  Alpaca → Paper Trading → API Keys
                </a>
                . NUMIROCK verifies <code>status == PAPER_ONLY</code> before
                accepting.
              </p>
              <div>
                <Label
                  htmlFor="alpaca-key-id-input"
                  className="text-[10px] uppercase tracking-widest text-[var(--aurion-muted)]"
                >
                  Key ID
                </Label>
                <Input
                  id="alpaca-key-id-input"
                  data-testid="alpaca-key-id-input"
                  autoComplete="off"
                  spellCheck={false}
                  value={keyId}
                  onChange={(e) => setKeyId(e.target.value)}
                  placeholder="PK... (paper key)"
                  className="font-mono text-sm"
                />
              </div>
              <div>
                <Label
                  htmlFor="alpaca-secret-input"
                  className="text-[10px] uppercase tracking-widest text-[var(--aurion-muted)]"
                >
                  Secret
                </Label>
                <Input
                  id="alpaca-secret-input"
                  data-testid="alpaca-secret-input"
                  type="password"
                  autoComplete="off"
                  spellCheck={false}
                  value={secret}
                  onChange={(e) => setSecret(e.target.value)}
                  placeholder="40-character secret"
                  className="font-mono text-sm"
                />
              </div>
              <div className="flex items-center justify-end gap-2">
                <Button
                  type="submit"
                  data-testid="alpaca-connect-btn"
                  disabled={submitting}
                  className="bg-[var(--aurion-gold)] text-black hover:bg-[var(--aurion-gold)]/90"
                >
                  {submitting ? "Connecting…" : "Connect"}
                </Button>
              </div>
            </form>
          </CardContent>
        </Card>
      )}

      <Alert
        className="border-[var(--aurion-border)] bg-[var(--aurion-panel)]/40"
        data-testid="alpaca-refuses-live"
      >
        <AlertTriangle className="h-4 w-4 text-yellow-400" />
        <AlertTitle>NUMIROCK refuses live-money keys</AlertTitle>
        <AlertDescription className="text-xs text-[var(--aurion-muted)]">
          If your Alpaca account returns <code>status == "ACTIVE"</code>
          (a real brokerage account) the connect is rejected. This is a
          non-negotiable guardrail — Auto-Mode and verdict-driven orders can
          only ever hit paper.
        </AlertDescription>
      </Alert>
    </div>
  );
}

function Field({ label, value, testid }) {
  return (
    <div>
      <div className="text-[10px] uppercase tracking-widest text-[var(--aurion-muted)]">
        {label}
      </div>
      <div className="mt-1 font-mono text-sm text-[var(--aurion-text)]" data-testid={testid}>
        {value ?? "—"}
      </div>
    </div>
  );
}
